import { EntityType } from "../../features/data/data-types";
import {
  billingPlans,
  freePlan,
  BillingPlan,
  PlanLimit,
} from "../../features/billing/plan";

const allPlans: BillingPlan[] = [freePlan, ...billingPlans];

// Pages per catalog by plan (encoded in plan.features as HTML, so hardcoded here)
const planPageLimits: Partial<Record<string, string>> = {
  free: "20 pages per catalog",
  parked: "20 pages per catalog",
  lite: "50 pages per catalog",
  pro: "unlimited pages per catalog",
  premium: "unlimited pages per catalog",
};

export type TierOption = {
  id: string;
  name: string;
  price: number;
  /**
   * The plan's price scales with configurable limits (Pro's sliders add
   * `unitPrice` per product/catalog/storage/user on top of the base), so
   * `price` is a floor, not the amount. Quote it as "from $X" — the same
   * `pro` flag drives `buildPriceLayer`'s add-on loop.
   */
  fromPrice: boolean;
  limitLabel: string;
  benefits: string[];
};

export type NextTierInfo = TierOption & {
  /** Present when a 2-level skip exists (e.g., Free→Pro when default is Free→Lite). Powers P2.2 skip-level upgrade. */
  skipTier?: TierOption;
};

/** Extra capacity buyable on the plan the user is ALREADY on. See `getPlanExpansion`. */
export type PlanExpansion = {
  planId: string;
  planName: string;
  /** Top of the slider, in the same unit the wall's `limit` prop uses (MB for storage). */
  maxLimit: number;
  /** Top of the slider for display, in the plan's own unit — "6 users", "5 GB storage". */
  maxLabel: string;
  /** Monthly price of one more unit, when the plan publishes one. */
  unitPrice?: number;
  /** Singular noun for that unit — "user", "product", "GB". */
  unitNoun: string;
};

/** Singular noun per plan limit, for "$5 per extra user per month". */
const limitNouns: Record<string, string> = {
  products: "product",
  catalogs: "catalog",
  storage: "GB",
  users: "user",
};

/**
 * Extra capacity the user can buy WITHOUT changing plan.
 *
 * Pro prices four of its limits with sliders (`marks` + `unitPrice`), so a Pro
 * customer who fills one is a $1-$5 upsell on the plan they already own, not a
 * $199/mo Premium candidate. `getNextTierInfo` only ever walks to the next
 * PLAN and never looks at the current plan's own sliders, so every such wall
 * quoted Premium: 30 impressions to 15 paying Pro accounts in the 60 days to
 * 2026-08-26, and not one upgrade click. Account #328472 bought Pro annual and
 * hit the seats wall 7 minutes later, invited the colleague his plan already
 * covered, was shown Premium, and deleted the invite 26 seconds after sending
 * it. That is the case that surfaced this.
 *
 * Returns null when the plan has no slider for this limit (Free, Lite,
 * Premium, and every credits wall) or when the account has already bought its
 * way to the top mark. Both are genuine next-plan cases, so the caller keeps
 * its existing tier pitch untouched.
 */
export function getPlanExpansion(
  currentPlanCode: string | undefined,
  type: EntityType,
  currentLimit: number,
): PlanExpansion | null {
  const plan = allPlans.find((p) => p.id === currentPlanCode);
  if (!plan) return null;

  const limitName = entityToLimitName(type);
  if (!limitName) return null;

  const planLimit = plan.limits.find((l) => l.name === limitName);
  if (!planLimit?.marks?.length) return null;

  const topMark = Math.max(...planLimit.marks);

  // Plan marks carry the plan's own unit; the `limit` the wall is handed is
  // always MB for storage (`limits.space`). Normalise before comparing, or a
  // Pro account on 1000 MB reads as already past the 5 GB top mark and the
  // expansion silently disappears for the one limit that needs it most.
  const perUnit = planLimit.unit === "GB" ? 1000 : 1;
  if (topMark * perUnit <= currentLimit) return null;

  const unit = planLimit.unit ? ` ${planLimit.unit}` : "";
  return {
    planId: plan.id,
    planName: plan.name,
    maxLimit: topMark * perUnit,
    maxLabel: `${topMark}${unit} ${limitName}`,
    unitPrice: planLimit.unitPrice,
    unitNoun: limitNouns[limitName] ?? limitName,
  };
}

/**
 * Get next tier info for a given plan and limit type.
 * Returns the next plan's name, price, a label for the specific limit being
 * upgraded, top benefit strings, and (optionally) a skip-level option.
 */
export function getNextTierInfo(
  currentPlanCode: string | undefined,
  type: EntityType,
): NextTierInfo | null {
  const currentPlan = allPlans.find((p) => p.id === currentPlanCode);
  if (!currentPlan) return null;

  const upgradeCandidates = allPlans
    .filter((p) => p.pos > currentPlan.pos && p.id !== "parked")
    .sort((a, b) => a.pos - b.pos);

  const nextPlan = upgradeCandidates[0];
  if (!nextPlan) return null;

  const next = buildTierOption(currentPlan, nextPlan, type);
  if (!next) return null;

  const skipPlan = upgradeCandidates[1];
  const skipTier = skipPlan
    ? buildTierOption(currentPlan, skipPlan, type)
    : undefined;

  return skipTier ? { ...next, skipTier } : next;
}

/** Build a single TierOption (next or skip) for a given target plan and entity type. */
function buildTierOption(
  currentPlan: BillingPlan,
  targetPlan: BillingPlan,
  type: EntityType,
): TierOption | null {
  const benefits = getBenefits(currentPlan, targetPlan, type);
  const base = {
    id: targetPlan.id,
    name: targetPlan.name,
    price: targetPlan.price,
    fromPrice: !!targetPlan.pro,
    benefits,
  };

  if (type === "pages") {
    const nextPageLabel =
      planPageLimits[targetPlan.id] ?? "more pages per catalog";
    return { ...base, limitLabel: nextPageLabel };
  }

  if (type === "variants" || type === "stores") {
    return { ...base, limitLabel: "expanded limits and more features" };
  }

  if (type === "ai_credits") {
    if (!targetPlan.aiCredits) return null;
    return {
      ...base,
      limitLabel: `${targetPlan.aiCredits} AI credits per month`,
    };
  }

  const limitName = entityToLimitName(type);
  if (!limitName) return null;

  const nextLimit = targetPlan.limits.find((l) => l.name === limitName);
  if (!nextLimit) return null;

  const unit = nextLimit.unit ? ` ${nextLimit.unit}` : "";
  return { ...base, limitLabel: `${nextLimit.limit}${unit} ${limitName}` };
}

/** A plan sized to a known number of products, with the price that size costs. */
export type SizedTier = {
  id: string;
  name: string;
  /** Monthly price AT this size — Pro's slider add-ons included, not a "from". */
  price: number;
  /** Products the plan covers once sized. Pro is snapped up to a slider mark. */
  products: number;
  /** True when the biggest plan still does not reach `needed`. */
  short: boolean;
};

/**
 * The cheapest plan that actually holds `needed` products.
 *
 * `getNextTierInfo` walks one step up the ladder and stops, which is right for a wall that only has to
 * unblock the next product. It is wrong for a wall that has to name the plan which imports a whole store:
 * a merchant with 2,000 products shown "Lite $19 — 100 products" is being quoted a plan that leaves 1,900
 * of them behind, and one shown "Pro $49" is being quoted a price that does not exist at their size.
 *
 * S79 Ph C — this is what makes "Pro imports them all" a true sentence. Pro's product limit is a slider
 * (500 → 6000 at $0.02/product/month), so the tier is snapped to the smallest mark that covers the store
 * and priced there. `ChangePlan` is handed the same number via `?products=`, so the card the merchant
 * lands on is set to the size the wall quoted.
 *
 * Returns null when the account is already on a plan that covers it, or is on the top plan.
 */
export function getTierForProducts(
  currentPlanCode: string | undefined,
  needed: number,
): SizedTier | null {
  const currentPlan = allPlans.find((p) => p.id === currentPlanCode);
  if (!currentPlan) return null;

  const candidates = allPlans
    .filter((p) => p.pos > currentPlan.pos && p.id !== "parked")
    .sort((a, b) => a.pos - b.pos);
  if (!candidates.length) return null;

  for (const plan of candidates) {
    const limit = plan.limits.find((l) => l.name === "products");
    if (!limit) continue;

    // A slider plan reaches its top mark, not its base limit. Pro's base is 500 and its reach is 6000;
    // skipping it because 500 < needed would quote Premium at $199 to a 600-product store.
    const marks = limit.marks ?? [];
    const reach = marks.length ? Math.max(...marks) : limit.limit;
    if (reach < needed) continue;

    const sized = marks.length
      ? (marks.find((m) => m >= needed) ?? reach)
      : limit.limit;
    const addOn = marks.length
      ? (sized - marks[0]) * (limit.unitPrice ?? 0)
      : 0;

    return {
      id: plan.id,
      name: plan.name,
      price: plan.price + addOn,
      products: sized,
      short: false,
    };
  }

  // Nothing reaches it. Quote the biggest plan honestly rather than silently offering one that does not
  // fit — the copy branches on `short` and drops the "imports them all" claim.
  const top = candidates[candidates.length - 1];
  const topLimit = top.limits.find((l) => l.name === "products");
  const topMarks = topLimit?.marks ?? [];
  return {
    id: top.id,
    name: top.name,
    price: top.price,
    products: topMarks.length
      ? Math.max(...topMarks)
      : (topLimit?.limit ?? 0),
    short: true,
  };
}

/**
 * Escape-hatch: where should "Manage existing X" send the user? (P2.4)
 * Returns null for types without a natural management page.
 */
export function getManageUrl(type: EntityType): string | null {
  switch (type) {
    case "products":
      return "/products";
    case "catalogs":
      return "/catalogs";
    case "images":
      return "/images";
    default:
      return null;
  }
}

/** Label for the P2.4 escape-hatch link. */
export function getManageLabel(type: EntityType): string {
  switch (type) {
    case "products":
      return "Manage existing products";
    case "catalogs":
      return "Manage existing catalogs";
    case "images":
      return "Manage existing images";
    default:
      return "Manage existing items";
  }
}

/** A plan limit in its base unit, so a GB row and an MB row compare as sizes. */
const limitInBaseUnit = (limit: PlanLimit | undefined): number =>
  limit ? limit.limit * (limit.unit === "GB" ? 1000 : 1) : 0;

/**
 * What the next plan adds BESIDES the limit the wall is about, at most three lines.
 *
 * The wall's headline already names the limit being hit ("Upgrade to Lite for 100
 * products"), so that row is left out of the "also unlocks" box rather than
 * repeated in it. AI credits come first: they used to be pushed last and cut by
 * the three-line cap, so a products wall never mentioned them, and they are the
 * one benefit a first-day converter is most likely to spend next (S34 § 8, gap
 * #5). Storage compares in MB on both sides: Pro's "1 GB" against Lite's "100 MB"
 * used to compare 1 against 100 and drop out as a downgrade.
 */
function getBenefits(
  currentPlan: BillingPlan,
  nextPlan: BillingPlan,
  type: EntityType,
): string[] {
  const benefits: string[] = [];
  const hitLimit = entityToLimitName(type);

  if (
    type !== "ai_credits" &&
    nextPlan.aiCredits &&
    nextPlan.aiCredits !== currentPlan.aiCredits
  ) {
    benefits.push(`${nextPlan.aiCredits} AI credits per month`);
  }

  for (const nextLimit of nextPlan.limits) {
    if (nextLimit.name === hitLimit) continue;
    const currentLimit = currentPlan.limits.find(
      (l) => l.name === nextLimit.name,
    );
    if (limitInBaseUnit(nextLimit) <= limitInBaseUnit(currentLimit)) continue;
    const unit = nextLimit.unit ? ` ${nextLimit.unit}` : "";
    switch (nextLimit.name) {
      case "products":
        benefits.push(`${nextLimit.limit} products`);
        break;
      case "catalogs":
        benefits.push(`${nextLimit.limit} catalogs`);
        break;
      case "storage":
        benefits.push(`${nextLimit.limit}${unit} storage`);
        break;
      case "users":
        benefits.push(`${nextLimit.limit} users`);
        break;
    }
  }

  return benefits.slice(0, 3);
}

/**
 * Get human-readable limit label, e.g. "20 products", "100 MB storage"
 */
export function getLimitLabel(type: EntityType, limit: number): string {
  switch (type) {
    case "products":
      return `${limit} products`;
    case "catalogs":
      return `${limit} catalogs`;
    case "images":
      return `${limit} MB storage`;
    // Seats are the one limit that routinely reads "1": every Free and Lite
    // plan is a single seat, so "1 users" is the common case, not the edge one.
    case "users":
      return `${limit} ${limit === 1 ? "user" : "users"}`;
    case "variants":
      return `${limit} variants`;
    case "pages":
      return `${limit} pages`;
    case "stores":
      return `${limit} stores`;
    // Credits are dollars everywhere else the app shows them (`formatCredits`,
    // the plan cards' "$20"); "2 AI credits" beside "$20 AI credits" read as
    // two different units on the same wall.
    case "ai_credits":
      return `$${limit} AI credits`;
    default:
      return `${limit} ${type}`;
  }
}

/**
 * Get accomplishment text when user hits a limit.
 */
export function getAccomplishmentText(
  type: EntityType,
  current: number,
): string {
  switch (type) {
    case "products":
      return `You've created ${current} products!`;
    case "catalogs":
      return `You've built ${current} catalogs!`;
    case "images":
      return `You've uploaded ${current} MB of images!`;
    case "users":
      return current === 1
        ? "You've used your only seat!"
        : `Your team has grown to ${current} users!`;
    case "variants":
      return `You've added ${current} variants!`;
    case "pages":
      return `Your catalog has ${current} pages!`;
    case "stores":
      return `You've connected ${current} stores!`;
    case "ai_credits":
      // Don't show the numeric count — the `current` value reflects only the monthly
      // allocation, not top-ups or regional welcome grants, so "You've used 2 AI credits"
      // is misleading for users who actually burned through more total.
      return "You've used all your AI credits!";
    default:
      return `You've reached your ${type} limit!`;
  }
}

/**
 * Map EntityType to PlanLimit name (for plan.limits array lookups).
 */
function entityToLimitName(type: EntityType): string | null {
  switch (type) {
    case "products":
      return "products";
    case "catalogs":
      return "catalogs";
    case "images":
      return "storage";
    case "users":
      return "users";
    default:
      return null;
  }
}
